import { useState, useEffect, useRef } from "react"
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, ChevronDown, ChevronRight, User, X } from "lucide-react"
import { format } from "date-fns"
import { Helmet } from 'react-helmet';
import DateSlotModal from "./DateSlotModal";
import AddressModal from "./AddressModal"
import MemberModal from "./MemberModal";
import authService from '../../../Services/authService';
import Done from '../../Assets/images/Done.png';
import { useCart } from "../../../Services/CartContext";
import CouponsModal from "../Offers/CouponsModal";
import DiscountIcon from "../../Assets/Icons/DiscountIcon";
import SEOSchema from '../../../utils/SEOSchema';


export default function CartPage() {
  const { fetchCartCount } = useCart();
  const [isDateModalOpen, setIsDateModalOpen] = useState(false)
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false)
  const [currentCartItemId, setCurrentCartItemId] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [selectedAddress, setSelectedAddress] = useState(null)
  const [addresses, setAddresses] = useState([])
  const [cartItems, setCartItems] = useState([]);  // Already initialized as an empty array
  const [members, setMembers] = useState([]);
  const [openDropdownItemId, setOpenDropdownItemId] = useState(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [dateTimeError, setDateTimeError] = useState("");
  const [addressError, setAddressError] = useState("");
  const [addressList, setAddressList] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [visitTypeSelections, setVisitTypeSelections] = useState({});
  // Ref for dropdown to handle outside clicks
  const dropdownRef = useRef(null)
  const token = authService.getCurrentUsertoken();
  const [showConfetti, setShowConfetti] = useState(false);
  const [paymentMode, setPaymentMode] = useState('online'); // default to online



  const [couponCode, setCouponCode] = useState('');
  const [message, setMessage] = useState('');
  const [discount, setDiscount] = useState(null);
  const [offer_id, setOfferid] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  //const [gstAmount, setSelectedCoupon] = useState(null);
  const [couponDetails, setCouponDetails] = useState(null);


  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Load saved appointment data
    fetchAddressList();
    loadCart();
    familyMembers();
    const savedDate = localStorage.getItem("slotdate");
    const savedSlot = localStorage.getItem("slottime");

    if (savedDate && savedSlot) {
      const parsedDate = new Date(savedDate);
      if (!isNaN(parsedDate)) {
        setSelectedDate(parsedDate);
        setSelectedSlot(savedSlot);
      } else {
        console.error("Invalid date found in localStorage:", savedDate);
      }
    }
  }, []);

  const formatDateTime = (date) => {
    return format(date, "yyyy-MM-dd HH:mm:ss");
  };

  const loadCart = () => {
    const url = new URL(authService.getApiUrl() + 'cartdata');
    fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        user_id: authService.getCurrentuser().id,
        organization_id: 1
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`, // Add Authorization header with Bearer token
      },
    })
      .then((res) => {
        if (res.status === 401) {
          authService.clientlogout();
          window.location.href = "/login";
        } else {
          return res.json();
        }
      })
      .then((result) => {
        const cartItemsWithMembers = result.data.cartitems.map(item => ({
          ...item,
          members: item.members || [] // Initialize members array if not present
        }));
        setCartItems(cartItemsWithMembers);
        
        // Auto-select visit types based on available options
        const defaultVisitTypes = {};
        cartItemsWithMembers.forEach((item, index) => {
          for (let i = 0; i < item.quantity; i++) {
            const key = `${index}-${i}`;
            if (item.visit_type_id !== 2) {
              // Both Home and Diagnostic Center available - default to Home (1)
              defaultVisitTypes[key] = "1";
            } else {
              // Only Diagnostic Center available - auto-select it (2)
              defaultVisitTypes[key] = "2";
            }
          }
        });
        setVisitTypeSelections(defaultVisitTypes);
      })
      .catch((err) => {
        console.log(err.message);
      });
  };


  const familyMembers = () => {
    const url = new URL(authService.getApiUrl() + 'userfamilylist');
    fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        user_id: authService.getCurrentuser().id,
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`, // Add Authorization header with Bearer token
      },
    })
      .then((res) => {
        if (res.status === 401) {
          authService.clientlogout();
          window.location.href = "/login";
        } else {
          return res.json();
        }
      })
      .then((result) => {
        setMembers(result.data);
        
        // Auto-assign first member to all cart items if no members assigned
        if (result.data.length > 0) {
          setCartItems(prevItems => 
            prevItems.map(item => {
              if (!item.members || item.members.length === 0) {
                const membersArray = new Array(item.quantity).fill(result.data[0]);
                return { ...item, members: membersArray };
              }
              return item;
            })
          );
        }
      })
      .catch((err) => {
        console.log(err.message);
      });
  };


  const fetchAddressList = () => {
    const url = new URL(authService.getApiUrl() + 'Addresslist')

    fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        user_id: authService.getCurrentuser().id,
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (res.status === 401) {
          authService.clientlogout()
          window.location.href = "/login"
        } else {
          return res.json()
        }
      })
      .then((result) => {
        const addressData = result.data.map((item) => ({
          id: item.id,
          flatNumber: item.building_name,
          area: item.area,
          landmark: item.landmark,
          state: item.state_name,
          city: item.city_name,
          phoneNumber: item.phone_number,
          pincode: item.pincode,
          isDefault: result.default_address?.id === item.id,
        }))

        setAddresses(addressData)

        const defaultAddress = addressData.find((addr) => addr.isDefault)
        if (defaultAddress) {
          setSelectedAddress(defaultAddress)
        }
      })
      .catch((err) => {
        console.error("Address fetch error:", err.message)
      })
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownItemId(null)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Save addresses to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("addresses", JSON.stringify(addresses))
  }, [addresses])

  // Save members to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("members", JSON.stringify(members))
  }, [members])

  // Calculate totals
  const subtotal = cartItems?.reduce?.((sum, item) => {
    const price = parseFloat(item.price * item.quantity) || 0;
    return sum + price;
  }, 0) || 0;

  // If coupon details exist, use their values
  const gstAmount = couponDetails?.gst ?? subtotal * 0.18;
  const total = couponDetails?.final_price_with_gst ?? (subtotal + gstAmount - (discount || 0));


  const handleDateConfirm = (date, slot) => {
    // setSelectedDate(date);
    // setSelectedSlot(slot);
    // const formattedDate = format(date, "yyyy-MM-dd"); // Save in standard format
    // authService.AppointmentSlotsSetup(formattedDate, slot);
  };

  const handleAddressSelect = (address) => {
    // If this is a new address, add it to the list
    if (!addresses.find((addr) => addr.id === address.id)) {
      setAddresses((prev) => {
        // If this is set as default, remove default from others
        const updatedAddresses = address.isDefault ? prev.map((addr) => ({ ...addr, isDefault: false })) : [...prev]

        return [...updatedAddresses, address]
      })
    }

    // Set as selected address
    setSelectedAddress(address)
  }

  const handleAddMember = (member) => {
    // Add member to the global list if not already present
    if (!members.find((m) => m.id === member.id)) {
      setMembers((prev) => [...prev, member])
    }

    // If we have a current cart item selected, add the member to it
    if (currentCartItemId !== null) {
      setCartItems((prevItems) =>
        prevItems.map((item) => {
          if (item.id === currentCartItemId) {
            // Check if member is already assigned to this item
            if (!item.members.some((m) => m.id === member.id)) {
              return {
                ...item,
                members: [...item.members, member],
              }
            }
          }
          return item
        }),
      )
    }

    // Close the modal
    setIsMemberModalOpen(false)
  }

  const openMemberModal = (itemId) => {
    setCurrentCartItemId(itemId)
    setIsMemberModalOpen(true)
  }

  const removeMemberFromItem = (itemId, memberId) => {
    setCartItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === itemId) {
          return {
            ...item,
            members: item.members.filter((m) => m.id !== memberId),
          }
        }
        return item
      }),
    )
  }

  const toggleMemberDropdown = (itemId) => {
    setOpenDropdownItemId(openDropdownItemId === itemId ? null : itemId)
    setCurrentCartItemId(itemId)
  }

  const selectExistingMember = (itemId, member) => {
    // Check if member is already assigned to this item
    const item = cartItems.find((item) => item.id === itemId)
    if (item && item.members.some((m) => m.id === member.id)) {
      // Member already assigned, show message
      alert(`${member.name} is already assigned to this test.`)
      return
    }

    // Add member to the cart item
    setCartItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === itemId) {
          return {
            ...item,
            members: [...item.members, member],
          }
        }
        return item
      }),
    )

    // Close dropdown
    setOpenDropdownItemId(null)
  }

  const checksoltsavailability = async (event) => {
    event.preventDefault();

    const savedDate = localStorage.getItem("slotdate");
    const savedSlot = localStorage.getItem("slottime");

    const url = new URL(authService.getApiUrl() + 'checkslots');

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({
          organization_id: 1,
          slot_date: savedDate,
          slot_time: savedSlot,
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (response.ok && result.data?.available) {
        // Slots available, proceed with order
        //handleSubmit(event);
        if (paymentMode === 'cod') {
          handleSubmitCOD(event); // COD path
        } else {
          handleSubmit(event); // Online payment path
        }
      } else {
        // Slots not available
        alert(result.data?.message || "Selected slot is not available. Please choose another.");
      }
    } catch (error) {
      console.error("Slot check failed:", error.message);
      alert("Failed to check slot availability. Please try again.");
    }
  };


  const handleSubmit = async (event) => {
    event.preventDefault();
    let hasError = false;
    const errors = {};

    const savedDate = localStorage.getItem("slotdate");
    const savedSlot = localStorage.getItem("slottime");

    if (!selectedAddress) {
      errors.address = "Please select an address.";
      hasError = true;
    }

    if (!savedDate || !savedSlot) {
      errors.dateTime = "Please select a date and slot.";
      hasError = true;
    }

    cartItems.forEach((item, index) => {
      for (let i = 0; i < item.quantity; i++) {
        const key = `${index}-${i}`;
        if (!Array.isArray(item.members) || !item.members[i]) {
          errors[`item-${key}`] = "Please assign at least one family member.";
          hasError = true;
        }
        if (!visitTypeSelections[key]) {
          errors[`visitType-${key}`] = "Please select a visit type.";
          hasError = true;
        }
      }
    });

    setValidationErrors(errors);

    if (hasError) {
      alert("Please correct the errors before placing the order.");
      return;
    }

    const Organization_id = 1;
    const now = new Date();

    const tests = [];

    cartItems.forEach((item, itemIndex) => {
      for (let i = 0; i < item.quantity; i++) {
        const key = `${itemIndex}-${i}`;
        const visitTypeId = visitTypeSelections[key];
        if (Array.isArray(item.members) && item.members[i]) {
          tests.push({
            test_name: item.name,
            test_id: item.test_id,
            quantity: 1,
            price: item.price,
            preparation: item.preparation,
            type: item.type,
            visit_type_id: visitTypeId,
            family_member_id: item.members[i].id,
          });
        }
      }
    });

    const uniqueVisitTypes = [...new Set(tests.map(test => test.visit_type_id))];

    // Prepare order data (but don’t submit yet)
    const orderPayload = {
      user_id: authService.getCurrentuser().id,
      total_price: subtotal,
      tax_amount: gstAmount,
      price: total,
      due_amount: 0,
      payment_mode: paymentMode,
      date_time: formatDateTime(now),
      slot_date: formatDateTime(savedDate),
      slot_time: savedSlot,
      status: "pending",
      address_id: selectedAddress.id,
      organization_id: Organization_id,
      order_items: tests,
      visit_type: uniqueVisitTypes,
      coupon_code: couponCode || "",
      discount_price: discount || 0,
      offer_id: offer_id || 0,
    };

    try {
      const options = {
        key: "rzp_test_w3KijNwpNo1wvt", // Replace with your actual Razorpay Key ID
        amount: Math.round(total * 100), // amount in paise
        currency: "INR",
        name: "Pranaam365",
        description: "Lab Test Booking Payment",
        handler: async function (response) {
          // Payment successful, now submit the order
          const finalOrderPayload = {
            ...orderPayload,
            transaction_id: response.razorpay_payment_id,
          };

          const orderRes = await fetch(authService.getApiUrl() + 'Addorder', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(finalOrderPayload),
          });

          const result = await orderRes.json();

          if (orderRes.ok) {
            setIsModalVisible(true);
            authService.RemoveSlots();
            await fetchCartCount();
          } else {
            alert(`Your payment was successful, but the selected time slot has already been booked by someone else.\n\nYour transaction ID is ${response.razorpay_payment_id}. Please choose a different slot or contact support.`);
            await fetchCartCount();
            navigate('/orders');
          }
        },
        prefill: {
          name: authService.getCurrentuser().user_name || "",
          email: authService.getCurrentuser().email || "",
          contact: authService.getCurrentuser().phone_number || "",
        },
        theme: {
          color: "#951C25",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Payment failed:", error.message);
      alert("Something went wrong. Please try again.");
    }
  };

  const handleSubmitCOD = async (event) => {
    event.preventDefault();
    let hasError = false;
    const errors = {};

    const savedDate = localStorage.getItem("slotdate");
    const savedSlot = localStorage.getItem("slottime");

    if (!selectedAddress) {
      errors.address = "Please select an address.";
      hasError = true;
    }

    if (!savedDate || !savedSlot) {
      errors.dateTime = "Please select a date and slot.";
      hasError = true;
    }

    cartItems.forEach((item, index) => {
      for (let i = 0; i < item.quantity; i++) {
        const key = `${index}-${i}`;

        // Validate members array
        if (!Array.isArray(item.members) || !item.members[i]) {
          errors[`item-${key}`] = "Please assign at least one family member.";
          hasError = true;
        }

        if (!visitTypeSelections[key]) {
          errors[`visitType-${key}`] = "Please select a visit type.";
          hasError = true;
        }
      }
    });

    setValidationErrors(errors);

    if (hasError) {
      alert("Please correct the errors before placing the order.");
      return;
    }

    const Organization_id = 1;
    const now = new Date();

    const tests = [];

    cartItems.forEach((item, itemIndex) => {
      for (let i = 0; i < item.quantity; i++) {
        const key = `${itemIndex}-${i}`;
        const visitTypeId = visitTypeSelections[key];

        if (Array.isArray(item.members) && item.members[i]) {
          tests.push({
            test_name: item.name,
            test_id: item.test_id,
            quantity: 1,
            price: item.price,
            preparation: item.preparation,
            type: item.type,
            visit_type_id: visitTypeId,
            family_member_id: item.members[i].id,
          });
        }
      }
    });

    const uniqueVisitTypes = [...new Set(tests.map(test => test.visit_type_id))];

    const orderPayload = {
      user_id: authService.getCurrentuser().id,
      total_price: subtotal,
      tax_amount: gstAmount,
      due_amount: total,
      payment_mode: paymentMode,
      price: total,
      date_time: formatDateTime(now),
      slot_date: formatDateTime(savedDate),
      slot_time: savedSlot,
      status: "pending",
      address_id: selectedAddress.id,
      organization_id: Organization_id,
      order_items: tests,
      visit_type: uniqueVisitTypes,
      coupon_code: couponCode || "",
      discount_price: discount || 0,
      offer_id: offer_id || 0,
      transaction_id: ""
    };

    try {
      const response = await fetch(authService.getApiUrl() + 'Addorder', {
        method: 'POST',
        body: JSON.stringify(orderPayload),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (response.ok) {
        setIsModalVisible(true);
        authService.RemoveSlots();
        await fetchCartCount();
      } else {
        if (response.status === 409) {
          alert(result.message || "Conflict: Duplicate order or slot unavailable.");
        } else {
          alert(result.message || "Failed to place order. Please try again.");
        }

        console.error(`Error ${response.status}:`, result.message);
      }
    } catch (error) {
      console.error("Order submission failed:", error.message);
      alert("Something went wrong while placing the order. Please try again.");
    }
  };

  // const handleSubmit = async (event) => {
  //   event.preventDefault();
  //   let hasError = false;
  //   const errors = {};

  //   const savedDate = localStorage.getItem("slotdate");
  //   const savedSlot = localStorage.getItem("slottime");

  //   if (!selectedAddress) {
  //     errors.address = "Please select an address.";
  //     hasError = true;
  //   }

  //   if (!savedDate || !savedSlot) {
  //     errors.dateTime = "Please select a date and slot.";
  //     hasError = true;
  //   }

  //   cartItems.forEach((item, index) => {
  //     for (let i = 0; i < item.quantity; i++) {
  //       const key = `${index}-${i}`;

  //       // Validate members array
  //       if (!Array.isArray(item.members) || !item.members[i]) {
  //         errors[`item-${key}`] = "Please assign at least one family member.";
  //         hasError = true;
  //       }

  //       if (!visitTypeSelections[key]) {
  //         errors[`visitType-${key}`] = "Please select a visit type.";
  //         hasError = true;
  //       }
  //     }
  //   });

  //   setValidationErrors(errors);

  //   if (hasError) {
  //     alert("Please correct the errors before placing the order.");
  //     return;
  //   }

  //   const Organization_id = 1;
  //   const now = new Date();

  //   const tests = [];

  //   cartItems.forEach((item, itemIndex) => {
  //     for (let i = 0; i < item.quantity; i++) {
  //       const key = `${itemIndex}-${i}`;
  //       const visitTypeId = visitTypeSelections[key];

  //       if (Array.isArray(item.members) && item.members[i]) {
  //         tests.push({
  //           test_name: item.name,
  //           test_id: item.test_id,
  //           quantity: 1,
  //           price: item.price,
  //           preparation: item.preparation,
  //           type: item.type,
  //           visit_type_id: visitTypeId,
  //           family_member_id: item.members[i].id,
  //         });
  //       }
  //     }
  //   });

  //   const uniqueVisitTypes = [...new Set(tests.map(test => test.visit_type_id))];

  //   const orderPayload = {
  //     user_id: authService.getCurrentuser().id,
  //     total_price: subtotal,
  //     tax_amount: gstAmount,
  //     price: total,
  //     date_time: formatDateTime(now),
  //     slot_date: formatDateTime(savedDate),
  //     slot_time: savedSlot,
  //     status: "pending",
  //     address_id: selectedAddress.id,
  //     organization_id: Organization_id,
  //     order_items: tests,
  //     visit_type: uniqueVisitTypes,
  //     coupon_code: couponCode || "",
  //     discount_price: discount || 0,
  //     offer_id: offer_id || 0,
  //   };

  //   try {
  //     const response = await fetch(authService.getApiUrl() + 'Addorder', {
  //       method: 'POST',
  //       body: JSON.stringify(orderPayload),
  //       headers: {
  //         Accept: 'application/json',
  //         'Content-Type': 'application/json; charset=UTF-8',
  //         Authorization: `Bearer ${token}`,
  //       },
  //     });

  //     const result = await response.json();

  //     if (response.ok) {
  //       setIsModalVisible(true);
  //       authService.RemoveSlots();
  //       await fetchCartCount();
  //     } else {
  //       if (response.status === 409) {
  //         alert(result.message || "Conflict: Duplicate order or slot unavailable.");
  //       } else {
  //         alert(result.message || "Failed to place order. Please try again.");
  //       }

  //       console.error(`Error ${response.status}:`, result.message);
  //     }
  //   } catch (error) {
  //     console.error("Order submission failed:", error.message);
  //     alert("Something went wrong while placing the order. Please try again.");
  //   }
  // };

  const handleCoupon = async (selectedCoupon = null) => {
    let codeToApply = '';

    if (selectedCoupon && typeof selectedCoupon === 'object') {
      codeToApply = couponCode;
    } else if (typeof couponCode === 'string') {
      codeToApply = selectedCoupon;
    } else {
      setMessage('Please enter a coupon code.');
      setDiscount(0);
      return;
    }

    try {
      const response = await fetch(authService.getApiUrl() + 'applycode', {
        method: 'POST',
        body: JSON.stringify({
          user_id: authService.getCurrentuser().id,
          coupon_code: codeToApply,
          order_total: subtotal,
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (result.status === "success") {
        playConfetti();

        setDiscount(result.discount_price || 0);
        setMessage(`Coupon applied! You got ${result.discount_percentage}% off.`);
        setOfferid(result.offer_id);
        setCouponCode(codeToApply); // update coupon code input too
        setSelectedCoupon({ coupon_code: codeToApply });
        setCouponDetails(result);
      } else {
        setDiscount(0);
        setCouponDetails(null);
        setMessage(result.message || "Invalid coupon code.");
      }
    } catch (error) {
      setDiscount(0);
      setCouponDetails(null);
      setMessage("Error applying coupon. Please try again.");
      console.error("Coupon error:", error);
    }
  };

  const playConfetti = () => {
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 1000); // Hide after 2 seconds
  };

  // Closes the coupon modal
  const handleCouponModalClose = () => {
    setIsCouponModalOpen(false);
  };

  const handleApplyCoupon = (coupon) => {
    setIsCouponModalOpen(false);
    handleCoupon(coupon.coupon_code); // call your existing API logic
  };

  const removeCartItem = (itemId) => {

    //setCartItems((prevItems) => prevItems.filter((item) => item.id !== itemId))

    fetch(authService.getApiUrl() + "cart/delete", {
      method: "PUT",
      body: JSON.stringify({
        user_id: authService.getCurrentuser().id,
        id: itemId,
      }),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=UTF-8",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then(() => {
        loadCart();
        fetchCartCount();
      })
      .catch((err) => {
        console.error("Error adding item to cart:", err);
      });
  }

  return (
    <>
      <Helmet>
        <title>Cart - Pranaam365</title>
        <meta name="description" content="Review your selected tests and complete your booking with Pranaam365." />
      </Helmet>
      
      <SEOSchema 
        type="webpage"
        data={{
          title: "Cart - Pranaam365",
          description: "Review and complete your medical test booking",
          url: window.location.href
        }}
      />
    
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Navigation */}


      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Back Button */}
        <Link to="#" onClick={() => window.history.back()} className="flex items-center text-[#4A4A4A] hover:text-[#951C25] mb-6">
          <ArrowLeft size={18} className="mr-2" />
          <span>Continue Shopping</span>
        </Link>

        {/* Page Title */}
        <h1 className="text-2xl font-semibold text-[#4A4A4A] mb-6">Your Cart</h1>

        {/* Date Selection */}
        <div className="bg-white rounded-lg border border-[#E0E0E0] p-5 mb-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <h2 className="text-base font-medium text-[#4A4A4A]">Date</h2>
            </div>
            <button onClick={() => setIsDateModalOpen(true)} className="text-sm text-[#951C25] hover:underline">
              Select
            </button>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-8 h-8 rounded-full bg-[#951C25]/10 flex items-center justify-center mr-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[#951C25]"
              >
                <rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect>
                <line x1="16" x2="16" y1="2" y2="6"></line>
                <line x1="8" x2="8" y1="2" y2="6"></line>
                <line x1="3" x2="21" y1="10" y2="10"></line>
              </svg>
            </div>
            {/* {selectedDate && selectedSlot ? ( */}
            <span className="text-sm text-[#4A4A4A]">
              {/* {format(selectedDate, "EEEE, MMM d, yyyy")} | {selectedSlot} */}
              {authService.getAppointmentSlots()}

            </span>
            {/* // ) : (
            //   <span className="text-sm text-[#4A4A4A]/70">N/A</span>
            // )} */}
            {validationErrors.dateTime && (
              <p className="text-red-600 text-sm mt-1">{validationErrors.dateTime}</p>
            )}
          </div>
        </div>

        {/* Address Selection */}
        <div className="bg-white rounded-lg border border-[#E0E0E0] p-5 mb-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <h2 className="text-base font-medium text-[#4A4A4A]">Address</h2>
            </div>
            <button onClick={() => setIsAddressModalOpen(true)} className="text-sm text-[#951C25] hover:underline">
              Select
            </button>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-8 h-8 rounded-full bg-[#951C25]/10 flex items-center justify-center mr-3">
              <MapPin size={16} className="text-[#951C25]" />
            </div>
            {selectedAddress ? (
              <div className="text-sm text-[#4A4A4A]">
                <p>
                  {selectedAddress.flatNumber}, {selectedAddress.area}, {selectedAddress.landmark}
                </p>
                <p>
                  {selectedAddress.city}, {selectedAddress.state} - {selectedAddress.pincode}
                </p>
              </div>
            ) : (
              <span className="text-sm text-[#4A4A4A]/70">No default address set</span>
            )}
            {validationErrors.address && (
              <p className="text-red-600 text-sm">{validationErrors.address}</p>
            )}
          </div>
        </div>

        {/* Cart Items */}
        {cartItems.map((data, index) => {
          const items = [];
          for (let i = 0; i < data.quantity; i++) {
            const key = `${index}-${i}`; // Unique key for each dropdown and visit type

            items.push(
              <div key={key} className="bg-white rounded-lg border border-[#E0E0E0] p-5 mb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-base font-medium text-[#4A4A4A]">{data.name}</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-medium text-[#4A4A4A]">₹{data.price}</span>
                    <button
                      onClick={() => removeCartItem(data.id)}
                      className="text-[#951C25] hover:text-[#951C25]/80 p-1"
                      title="Remove item"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>


                <div className="flex justify-between items-center relative">
                  <div className="relative inline-block z-10">
                    <select
                      className="appearance-none px-4 py-2 bg-[#951C25]/10 text-[#951C25] rounded-md text-sm hover:bg-[#951C25]/20 pr-8"
                      name="member"
                      required
                      value={data.members?.[i]?.id || ""}
                      onChange={(e) => {
                        const selectedMember = members.find((m) => m.id === parseInt(e.target.value));
                        if (!selectedMember) return;

                        setCartItems((prevItems) =>
                          prevItems.map((item, itemIndex) => {
                            if (itemIndex === index) {
                              const updatedMembers = [...(item.members || [])];
                              updatedMembers[i] = selectedMember; // Assign to current quantity slot
                              return { ...item, members: updatedMembers };
                            }
                            return item;
                          })
                        );
                      }}
                    >
                      <option value="">Select Member</option>
                      {members.map((item) => (
                        <option value={item.id} key={item.id}>{item.name}</option>
                      ))}
                    </select>

                    <ChevronDown
                      size={16}
                      className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#951C25]"
                    />
                  </div>

                  <button
                    onClick={() => openMemberModal()}
                    className="px-4 py-2 bg-[#951C25] text-white rounded-md text-sm"
                  >
                    ADD MEMBER
                  </button>
                </div>

                {validationErrors[`item-${key}`] && (
                  <p className="text-red-600 text-sm">
                    {validationErrors[`item-${key}`]}
                  </p>
                )}

                <>
                  <div className="mt-4 flex items-center gap-6">
                    {data.visit_type_id !== 2 && (
                      <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                        <input
                          type="radio"
                          name={`visitType-${key}`}
                          value="1"
                          className="accent-[#951C25]"
                          checked={visitTypeSelections[key] === "1"}
                          onChange={() =>
                            setVisitTypeSelections((prev) => ({
                              ...prev,
                              [key]: "1"
                            }))
                          }
                        />
                        Home
                      </label>
                    )}
                    <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                      <input
                        type="radio"
                        name={`visitType-${key}`}
                        value="2"
                        className="accent-[#951C25]"
                        checked={visitTypeSelections[key] === "2"}
                        onChange={() =>
                          setVisitTypeSelections((prev) => ({
                            ...prev,
                            [key]: "2"
                          }))
                        }
                      />
                      Diagnostic Center
                    </label>
                  </div>

                  {validationErrors[`visitType-${key}`] && (
                    <p className="text-red-600 text-sm mt-1">
                      {validationErrors[`visitType-${key}`]}
                    </p>
                  )}
                </>
              </div>
            );
          }
          return items;
        })}

        {/* Apply Coupon */}
        <div className="bg-white rounded-lg border border-[#E0E0E0] p-5 mb-4">
          <div className="flex justify-between items-center">
            <h2 className="text-base font-medium text-[#4A4A4A]">
              <DiscountIcon className="inline w-8 h-8 mr-2" /> Apply Coupon
            </h2>
            {/* <Link to="#" onClick={() => setIsCouponModalOpen(true)} className="flex items-center text-sm text-[#951C25] hover:underline">
              View all coupons
              <ChevronRight size={16} className="ml-1" />
            </Link> */}
            <button
              onClick={() => setIsCouponModalOpen(true)}
              className="flex items-center text-sm text-[#951C25] hover:underline"
            >
              View all coupons
              <ChevronRight size={16} className="ml-1" />
            </button>
          </div>
          <div className="mt-3">
            <div className="flex">
              <input
                type="text"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                className="flex-1 px-3 py-2 border border-[#E0E0E0] rounded-l-md focus:outline-none focus:ring-1 focus:ring-[#951C25] focus:border-[#951C25]"
              />
              <button
                className="px-4 py-2 bg-[#951C25] text-white rounded-r-md text-sm hover:bg-[#951C25]/90"
                onClick={handleCoupon}
              >
                Apply
              </button>
            </div>
            {message && (
              <p className={`mt-2 text-sm ${discount ? 'text-green-600' : 'text-red-600'}`}>
                {message}
              </p>
            )}
          </div>
        </div>

        {/* Payment Details */}
        <div className="bg-white rounded-lg border border-[#E0E0E0] p-5 mb-4">
          <h2 className="text-base font-medium text-[#4A4A4A] mb-4">Payment Detail</h2>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#4A4A4A]/70">Subtotal</span>
              <span className="text-sm text-[#4A4A4A]">₹{subtotal.toFixed(2)}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-[#4A4A4A]/70">GST 18%</span>
              <span className="text-sm text-[#4A4A4A]">₹{gstAmount.toFixed(2)}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-[#4A4A4A]/70">Discount</span>
              <span className="text-sm text-green-600">
                {discount ? `- ₹${discount.toFixed(2)}` : "-"}
              </span>
            </div>

            <div className="pt-3 border-t border-[#E0E0E0] flex justify-between items-center">
              <span className="text-base font-medium text-[#4A4A4A]">Total</span>
              <span className="text-base font-medium text-[#951C25]">₹{total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-white rounded-lg border border-[#E0E0E0] p-5 mb-6 hidden">
          <h2 className="text-base font-medium text-[#4A4A4A]">Payment Method</h2>
          {/* Payment method options would go here */}
          <div className="mb-4">
            {/* <label className="font-semibold">Select Payment Mode:</label> */}
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="payment_mode"
                  value="online"
                  checked={paymentMode === 'online'}
                  onChange={(e) => setPaymentMode(e.target.value)}
                />
                Online Payment
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="payment_mode"
                  value="cod"
                  checked={paymentMode === 'cod'}
                  onChange={(e) => setPaymentMode(e.target.value)}
                />
                Cash on Delivery (COD)
              </label>
            </div>
          </div>

        </div>

        {/* Checkout Footer */}
        <div className="bottom-0 bg-white border-t border-[#E0E0E0] p-4 flex justify-between items-center " style={{ zIndex: '999' }}>
          <div>
            <p className="text-sm text-[#4A4A4A]/70">Total</p>
            <p className="text-lg font-semibold text-[#4A4A4A]">
              ₹{total.toFixed(2)}
            </p>
          </div>
          {cartItems.length > 0 && (
            <button className="px-6 py-3 bg-[#951C25] text-white rounded-md text-sm font-medium hover:bg-[#951C25]/90 transition-colors" type="button" onClick={checksoltsavailability}>
              Place Order & Pay
            </button>
          )}
        </div>
      </div>

      {/* Date Selection Modal */}
      <DateSlotModal isOpen={isDateModalOpen} onClose={() => setIsDateModalOpen(false)} onConfirm={handleDateConfirm} />

      {/* Address Selection Modal */}
      <AddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        onSelect={handleAddressSelect}
        addresses={addresses}
        refreshAddressList={fetchAddressList}
      />

      {/* {isCouponModalOpen && (
      <CouponModal
        isOpen={isCouponModalOpen}
        onClose={() => setIsCouponModalOpen(false)}
       // onSelect={handleAddressSelect}

      />
      )} */}

      {/* Member Modal */}
      <MemberModal isOpen={isMemberModalOpen} onClose={() => setIsMemberModalOpen(false)} onAdd={handleAddMember} refreshFamilyList={familyMembers} />

      <div className="relative">
        {isModalVisible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Modal Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>

            {/* Modal Content */}
            <div className="relative bg-white rounded-3xl shadow-xl w-80 p-6 text-center z-10">
              {/* Success Icon */}
              <div className="flex justify-center items-center w-20 h-20 mx-auto rounded-full bg-purple-100 mb-4">
                <img src={Done} alt="done-img" />
              </div>

              {/* Message */}
              <p className="text-md font-medium text-gray-800 mb-6">
                Your order was placed successfully!
              </p>

              {/* OK Button */}
              <Link to="/orders">
                <button
                  type="button"
                  className="px-8 py-2 rounded-full bg-[#951C25] text-white font-semibold shadow-md hover:bg-purple-700 transition duration-200"
                >
                  OK
                </button>
              </Link>
            </div>
          </div>
        )}
      </div>


      {/* Coupons Modal */}
      <CouponsModal
        isOpen={isCouponModalOpen}
        onClose={handleCouponModalClose}
        onApply={handleApplyCoupon}
        appliedCouponCode={selectedCoupon?.coupon_code}
      />

      {showConfetti && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none">
          <img
            src="/images/coupon.gif"
            alt="Confetti Celebration"
            className="w-[90%] h-[90%] object-contain animate-blast"
          />
        </div>

      )}





    </div>
    </>
  )
}
